import { z } from 'zod'

import { CurrencyZ, ShortDistanceUnitsZ } from '@/types/common'
import { CountriesCode } from '@/types/locale'

import {
	ExpenseFileZ,
	ExpenseModelZ,
	ExpenseRequestAttendeeZ,
	ExpenseRequestZ,
	ExpenseResponseAttendeeZ,
	ExpenseResponseZ,
	UnitsOfLengthZ,
} from './expense'

const AddressApiZ = z.object({
	id: z.string(),
	full_address: z.string(),
})

const AddressZ = z.object({
	action: z.object({ id: z.string() }),
	full_address: z.string(),
})

const MileageRateZ = z.object({
	id: z.number(),
	country_code: z.enum(CountriesCode),
	country: z.string(),
	name: z.string(),
	currency: CurrencyZ,
	unit_of_length: UnitsOfLengthZ,
	amount: z.number().min(0),
	is_default: z.boolean(),
})
export type MileageRate = z.infer<typeof MileageRateZ>

const DistanceZ = z.object({
	calculatedValue: z.number().min(0),
	inputValue: z.string().regex(/^\d+(\.\d{0,1})?$/),
	showSuggestion: z.boolean(),
})

export const MileageModelZ = z
	.object({
		...ExpenseModelZ.shape,
		amount: z.number().min(0),
		attendees: z.array(ExpenseResponseAttendeeZ),
		date: z.date(),
		distance: DistanceZ,
		file: z.object({
			...ExpenseFileZ.partial().shape,
			original_file_url: ExpenseFileZ.shape.original_file_url,
		}),
		points: z.array(AddressZ).min(2),
		rate: MileageRateZ,
		unitOfLength: ShortDistanceUnitsZ,
	})
	.strict()
export type MileageModel = z.infer<typeof MileageModelZ>

const DistanceResponseZ = z.object({
	calculated_distance: z.number().min(0),
	distance: z.number().min(0),
	show_calculated_distance: z.boolean(),
})

export const MileageResponseZ = z
	.object({
		...ExpenseResponseZ.shape,
		...MileageModelZ.pick({ amount: true, attendees: true }).shape,
		date: z.iso.date(),
		destination_points: z.array(AddressApiZ).min(2),
		file_data: MileageModelZ.shape.file,
		mileage_rate: MileageModelZ.shape.rate,
		report_amount: z.number(),
		unit_of_length: UnitsOfLengthZ,
		...DistanceResponseZ.shape,
	})
	.strict()
export type MileageResponse = z.input<typeof MileageResponseZ>

export const MileageRequestZ = z
	.object({
		...ExpenseRequestZ.shape,
		...MileageResponseZ.pick({
			calculated_distance: true,
			currency: true,
			date: true,
			destination_points: true,
			distance: true,
			file_data: true,
			show_calculated_distance: true,
			unit_of_length: true,
		}).shape,
		attendees: z.array(ExpenseRequestAttendeeZ),
		mileage_rate_id: MileageResponseZ.shape.mileage_rate.shape.id,
	})
	.strict()
export type MileageRequest = z.output<typeof MileageRequestZ>
